import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { IsMobileContext } from 'src/hooks/useMobile';
import { Button } from 'src/components/system/Button';
import { createAdaptiveSimulation, createSimulation } from 'src/services/simulations';
import { listModulesByCategory, CategoryModule } from 'src/services/categories';
import {
  listMaterialsByCategory,
  listVideosByCategory,
  MaterialResource,
  VideoResource,
} from 'src/services/resources';
import { toast } from 'react-toastify';
import { useCourseCategories } from 'src/contexts/CourseCategoriesContext';
import { getFamilyMetadata } from 'src/shared/utils';
import { CFP_MODULES } from 'src/shared/data/cfp-modules';

function extractFileExtension(fileName?: string | null) {
  if (!fileName) {
    return null;
  }

  const parts = fileName.split('.');
  if (parts.length <= 1) {
    return null;
  }

  return parts.pop()?.toUpperCase() ?? null;
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 24px;
`;

const HeaderBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h1 {
    margin: 0;
    font-size: 1.75rem;
  }

  p {
    margin: 0;
    color: #4a5568;
  }
`;

const Section = styled.section`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const ModuleCard = styled.button<{ selected?: boolean }>`
  border: 1px solid ${({ selected }) => (selected ? '#222B82' : '#e3e8f1')};
  background: ${({ selected }) => (selected ? '#EEF2FF' : '#ffffff')};
  color: ${({ selected }) => (selected ? '#222B82' : '#1f2956')};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 120px;
  transition: border-color 0.2s ease, background 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  font-weight: 600;

  &:hover {
    border-color: #222b82;
  }
`;

const ModuleMeta = styled.span`
  font-size: 0.85rem;
  color: #56606f;
  font-weight: 500;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h2 {
    margin: 0;
    font-size: 1.25rem;
  }

  p {
    margin: 0;
    color: #4a5568;
  }
`;

const SizeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SizeButton = styled.button<{ active?: boolean }>`
  border: 1px solid ${({ active }) => (active ? '#222B82' : '#d6d6d6')};
  background: ${({ active }) => (active ? '#EEF2FF' : '#fff')};
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 500;
  color: ${({ active }) => (active ? '#222B82' : '#4a4a4a')};
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: #222B82;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const Feedback = styled.div<{ variant?: 'info' | 'error' }>`
  padding: 14px 16px;
  border-radius: 10px;
  background: ${({ variant }) => (variant === 'error' ? '#FDECEC' : '#F5F7FB')};
  color: ${({ variant }) => (variant === 'error' ? '#B42318' : '#4a5568')};
  font-size: 0.95rem;
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResourceCard = styled.article`
  border: 1px solid #e3e8f1;
  border-radius: 12px;
  padding: 16px;
  background: #f9faff;
  display: flex;
  flex-direction: column;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: #4a5568;
    font-size: 0.9rem;
  }
`;

const ResourceMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 0.85rem;
  color: #606f7b;
`;

const ResourceActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #222b82;
  font-size: 0.75rem;
  font-weight: 500;
`;

const Thumbnail = styled.img`
  width: 100%;
  max-width: 320px;
  border-radius: 10px;
`;

const VideoEmbed = styled.div`
  width: 100%;
  max-width: 720px;
  overflow: hidden;
  border-radius: 12px;

  iframe {
    width: 100%;
    min-height: 320px;
    border: 0;
    border-radius: 12px;
  }
`;

export function Families() {
  const navigate = useNavigate();
  const { isMobile } = useContext(IsMobileContext);
  const { slug: routeSlug } = useParams();
  const normalizedSlug = (routeSlug || '').toLowerCase();

  const { categories, loading: loadingCategories, error: categoryError } = useCourseCategories();
  const category = useMemo(
    () => categories.find(item => (item.slug ?? '').toLowerCase() === normalizedSlug),
    [categories, normalizedSlug],
  );
  const defaultFamilySlug = categories[0]?.slug ?? null;
  const metadata = getFamilyMetadata(category?.slug);

  const sizeOptions = useMemo(() => [20, 40, 60], []);
  const [moduleSize, setModuleSize] = useState<number>(sizeOptions[0]);
  const [completeSize, setCompleteSize] = useState<number>(sizeOptions[0]);
  const [creating, setCreating] = useState(false);

  const [modules, setModules] = useState<CategoryModule[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState<string | null>(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);
  const [selectedCfpModuleSlugs, setSelectedCfpModuleSlugs] = useState<string[]>([]);

  const [materials, setMaterials] = useState<MaterialResource[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsError, setMaterialsError] = useState<string | null>(null);

  const [videos, setVideos] = useState<VideoResource[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState<string | null>(null);

  const allowedDefaultSize =
    metadata && typeof metadata.defaultSimulationSize === 'number'
      ? sizeOptions.includes(metadata.defaultSimulationSize)
        ? metadata.defaultSimulationSize
        : null
      : null;
  const isCFPFamily = (category?.slug ?? '').toLowerCase() === 'cfp';
  useEffect(() => {
    if (!defaultFamilySlug) {
      return;
    }

    if (!routeSlug) {
      navigate(`/families/${defaultFamilySlug}`, { replace: true });
      return;
    }

    if (routeSlug && !category) {
      navigate(`/families/${defaultFamilySlug}`, { replace: true });
    }
  }, [category, defaultFamilySlug, navigate, routeSlug]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} | ART - Simulatoro`;
      const candidate = allowedDefaultSize ?? sizeOptions[0];
      const normalized = sizeOptions.includes(candidate) ? candidate : sizeOptions[0];
      setModuleSize(normalized);
      setCompleteSize(normalized);
    } else {
      document.title = 'Família não encontrada | ART - Simulatoro';
    }
  }, [allowedDefaultSize, category, sizeOptions]);

  const showModuleLoading = modulesLoading;
  const showModuleError = Boolean(modulesError);

  useEffect(() => {
    let active = true;
    setSelectedModuleIds([]);

    if (!category || !isCFPFamily) {
      setModules([]);
      setModulesLoading(false);
      setModulesError(null);
      return () => {
        active = false;
      };
    }

    setModulesLoading(true);
    setModulesError(null);

    const loadModules = async () => {
      try {
        const list = await listModulesByCategory(category.id);
        if (!active) return;
        setModules(list);
      } catch {
        if (!active) return;
        setModulesError('Não foi possível carregar os módulos desta família.');
        setModules([]);
      } finally {
        if (!active) return;
        setModulesLoading(false);
      }
    };

    loadModules();

    return () => {
      active = false;
    };
  }, [category, isCFPFamily]);

  useEffect(() => {
    let active = true;

    if (!category) {
      setMaterials([]);
      setVideos([]);
      return () => {
        active = false;
      };
    }

    const resourceKey = metadata?.resourcesKey ?? category.slug;

    if (!resourceKey) {
      setMaterials([]);
      setVideos([]);
      return () => {
        active = false;
      };
    }

    const loadMaterials = async () => {
      setMaterialsLoading(true);
      setMaterialsError(null);
      try {
        const list = await listMaterialsByCategory(resourceKey);
        if (active) {
          setMaterials(list);
        }
      } catch {
        if (active) {
          setMaterialsError('Não foi possível carregar os materiais desta família.');
          setMaterials([]);
        }
      } finally {
        if (active) {
          setMaterialsLoading(false);
        }
      }
    };

    const loadVideos = async () => {
      setVideosLoading(true);
      setVideosError(null);
      try {
        const list = await listVideosByCategory(resourceKey);
        if (active) {
          setVideos(list);
        }
      } catch {
        if (active) {
          setVideosError('Não foi possível carregar os vídeos desta família.');
          setVideos([]);
        }
      } finally {
        if (active) {
          setVideosLoading(false);
        }
      }
    };

    loadMaterials();
    loadVideos();

    return () => {
      active = false;
    };
  }, [category, metadata]);

  const toggleModuleSelection = (moduleId: number) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId],
    );
  };

  const toggleCfpModuleSelection = (slug: string) => {
    setSelectedCfpModuleSlugs(prev =>
      prev.includes(slug) ? prev.filter(item => item !== slug) : [...prev, slug],
    );
  };

  const handleCreateSimulation = async (payload: {
    size: number;
    moduleIds?: number[];
    moduleSlugs?: string[];
    onlyIncorrect?: boolean;
    onlyNew?: boolean;
  }) => {
    if (!category) {
      toast.error('Família de certificação não encontrada.');
      return;
    }

    try {
      setCreating(true);
      const response = await createSimulation(
        category.id,
        payload.size,
        true,
        payload.moduleIds ?? [],
        payload.moduleSlugs ?? [],
        payload.onlyIncorrect ?? false,
        payload.onlyNew ?? false,
      );
      navigate(`/simulate/${response.id}`);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        navigate('/auth/login');
        return;
      }
      toast.error('Não foi possível criar o simulado.');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateAdaptiveSimulation = async (size: number) => {
    if (!category) {
      toast.error('Família de certificação não encontrada.');
      return;
    }

    try {
      setCreating(true);
      const response = await createAdaptiveSimulation({
        size,
        categoryId: category.id,
        moduleSlugs: isCFPFamily && selectedCfpModuleSlugs.length > 0 ? selectedCfpModuleSlugs : undefined,
        includeReview: true,
        includeUnseen: true,
      });

      if (response?.rationale) {
        const reviewPool = response.rationale.review_pool ?? 0;
        const unseenPool = response.rationale.unseen_pool ?? 0;
        toast.info(`Simulado inteligente gerado: ${reviewPool} questões de revisão + ${unseenPool} inéditas no pool.`);
      }

      navigate(`/simulate/${response.id}`);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        navigate('/auth/login');
        return;
      }

      const apiMessage = typeof error?.response?.data?.message === 'string'
        ? error.response.data.message
        : null;

      toast.error(apiMessage ?? 'Não foi possível criar o simulado inteligente.');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenResource = (url?: string | null) => {
    if (!url) {
      toast.info('Conteúdo indisponível no momento.');
      return;
    }

    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const categoryUnavailable =
    !loadingCategories && !!routeSlug && !category && !categoryError;
  const showMaterialsEmptyState = !materialsLoading && !materialsError && materials.length === 0;
  const showVideosEmptyState = !videosLoading && !videosError && videos.length === 0;
  const totalSelectedModules = selectedModuleIds.length + selectedCfpModuleSlugs.length;
  const historyPath = category?.slug
    ? `/history?family=${encodeURIComponent(category.slug)}`
    : '/history';

  if (!category && !loadingCategories) {
    return (
      <Page>
        <Header>
          <h1>Família não encontrada</h1>
          <p>Não encontramos a certificação solicitada. Escolha uma opção no menu lateral.</p>
        </Header>
      </Page>
    );
  }

  return (
    <Page>
      <HeaderBar>
        <Header>
          <h1>{category?.fullName ?? category?.name}</h1>
          <p>{metadata?.description ?? category?.fullName ?? category?.name}</p>
        </Header>
        <Button
          type="button"
          width="180px"
          backgroundColor="#ffffff"
          borderColor="#222B82"
          buttonColor="#222B82"
          onClick={() => navigate(historyPath)}
          label={<span>Ver histórico</span>}
        />
      </HeaderBar>

      {isCFPFamily && (
        <Section>
          <SectionHeader>
            <h2>Simulador - Módulos</h2>
            <p>Escolha módulos específicos para focar seus simulados. Quando nenhum módulo estiver selecionado, o simulado considera toda a família.</p>
          </SectionHeader>

          {showModuleLoading && <Feedback>Carregando módulos...</Feedback>}
          {showModuleError && <Feedback variant="error">{modulesError}</Feedback>}
          {!showModuleLoading && !showModuleError && (
            <ModulesGrid>
              {CFP_MODULES.map(module => (
                <ModuleCard
                  key={module.slug}
                  type="button"
                  selected={selectedCfpModuleSlugs.includes(module.slug)}
                  onClick={() => toggleCfpModuleSelection(module.slug)}
                >
                  <span>{module.label}</span>
                  <ModuleMeta>{module.description}</ModuleMeta>
                </ModuleCard>
              ))}
            </ModulesGrid>
          )}

          <SizeRow>
            {sizeOptions.map(size => (
              <SizeButton
                key={`module-${size}`}
                active={moduleSize === size}
                onClick={() => setModuleSize(size)}
                type="button"
              >
                {size} questões
              </SizeButton>
            ))}
          </SizeRow>
          <ActionRow>
            <Button
              type="button"
              width={isMobile ? '100%' : '220px'}
              backgroundColor="#222B82"
              borderColor="#222B82"
              buttonColor="#fff"
              disabled={creating || !category}
              onClick={() => handleCreateSimulation({ size: moduleSize, moduleSlugs: selectedCfpModuleSlugs })}
              label={<span>Criar simulado</span>}
            />
            <Button
              type="button"
              width={isMobile ? '100%' : '220px'}
              backgroundColor="#B01B43"
              borderColor="#B01B43"
              buttonColor="#fff"
              disabled={creating || !category}
              onClick={() =>
                handleCreateSimulation({
                  size: moduleSize,
                  moduleSlugs: selectedCfpModuleSlugs,
                  onlyIncorrect: true,
                })
              }
              label={<span>Criar simulado com questões que errei</span>}
            />
            <Button
              type="button"
              width={isMobile ? '100%' : '220px'}
              backgroundColor="#B01B43"
              borderColor="#B01B43"
              buttonColor="#fff"
              disabled={creating || !category}
              onClick={() =>
                handleCreateSimulation({
                  size: moduleSize,
                  moduleSlugs: selectedCfpModuleSlugs,
                  onlyNew: true,
                })
              }
              label={<span>Criar simulado com questões que não fiz</span>}
            />
          </ActionRow>

          {totalSelectedModules > 0 && (
            <Feedback>
              O simulado será gerado apenas com os {totalSelectedModules} módulo(s) selecionado(s).
            </Feedback>
          )}
        </Section>
      )}

      <Section>
        <SectionHeader>
          <h2>Simulador - Completo</h2>
          <p>Monte um simulado completo para toda a família certificadora, incluindo todos os módulos disponíveis.</p>
        </SectionHeader>

        {loadingCategories && <Feedback>Carregando famílias...</Feedback>}
        {categoryError && !loadingCategories && <Feedback variant="error">{categoryError}</Feedback>}
        {categoryUnavailable && (
          <Feedback variant="error">
            Ainda não encontramos questões disponíveis para esta família. Volte mais tarde.
          </Feedback>
        )}

        <SizeRow>
          {sizeOptions.map(size => (
            <SizeButton
              key={`complete-${size}`}
              active={completeSize === size}
              onClick={() => setCompleteSize(size)}
              type="button"
            >
              {size} questões
            </SizeButton>
          ))}
        </SizeRow>
        <ActionRow>
          <Button
            type="button"
            width={isMobile ? '100%' : '220px'}
            backgroundColor="#222B82"
            borderColor="#222B82"
            buttonColor="#fff"
            disabled={creating || !category}
            onClick={() => handleCreateSimulation({ size: completeSize })}
            label={<span>Criar simulado</span>}
          />
          <Button
            type="button"
            width={isMobile ? '100%' : '220px'}
            backgroundColor="#B01B43"
            borderColor="#B01B43"
            buttonColor="#fff"
            disabled={creating || !category}
            onClick={() =>
              handleCreateSimulation({
                size: completeSize,
                onlyIncorrect: true,
              })
            }
            label={<span>Criar simulado com questões que errei</span>}
          />
          <Button
            type="button"
            width={isMobile ? '100%' : '220px'}
            backgroundColor="#B01B43"
            borderColor="#B01B43"
            buttonColor="#fff"
            disabled={creating || !category}
            onClick={() =>
              handleCreateSimulation({
                size: completeSize,
                onlyNew: true,
              })
            }
            label={<span>Criar simulado com questões que não fiz</span>}
          />
          <Button
            type="button"
            width={isMobile ? '100%' : '220px'}
            backgroundColor="#0F7B53"
            borderColor="#0F7B53"
            buttonColor="#fff"
            disabled={creating || !category}
            onClick={() => handleCreateAdaptiveSimulation(completeSize)}
            label={<span>Criar simulado inteligente</span>}
          />
        </ActionRow>
      </Section>

      <Section>
        <SectionHeader>
          <h2>Materiais</h2>
          <p>Acesse resumos, planilhas e guias para aprofundar seus estudos.</p>
        </SectionHeader>

        {materialsLoading && <Feedback>Carregando materiais...</Feedback>}
        {materialsError && !materialsLoading && <Feedback variant="error">{materialsError}</Feedback>}
        {showMaterialsEmptyState && <Feedback>Nenhum material disponível no momento.</Feedback>}

        {!materialsLoading && !materialsError && materials.length > 0 && (
          <ResourceList>
            {materials.map(material => {
              const extension = extractFileExtension(material.fileName);

              return (
                <ResourceCard key={material.id}>
                  <h3>{material.title}</h3>
                  {material.description && <p>{material.description}</p>}
                  <ResourceMeta>
                    {extension && <Tag>{extension}</Tag>}
                    {material.fileName && <span>{material.fileName}</span>}
                    {material.updatedAt && <span>Atualizado em {material.updatedAt}</span>}
                  </ResourceMeta>
                  <ResourceActions>
                    <Button
                      type="button"
                      width={isMobile ? '100%' : '180px'}
                      backgroundColor="#ffffff"
                      borderColor="#222B82"
                      buttonColor="#222B82"
                      onClick={() => handleOpenResource(material.url)}
                      label={<span>Baixar material</span>}
                    />
                  </ResourceActions>
                </ResourceCard>
              );
            })}
          </ResourceList>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <h2>Vídeos</h2>
          <p>Revise aulas gravadas e lives comentadas pelo time ART.</p>
        </SectionHeader>

        {videosLoading && <Feedback>Carregando vídeos...</Feedback>}
        {videosError && !videosLoading && <Feedback variant="error">{videosError}</Feedback>}
        {showVideosEmptyState && <Feedback>Nenhum vídeo disponível no momento.</Feedback>}

        {!videosLoading && !videosError && videos.length > 0 && (
          <ResourceList>
            {videos.map(video => {
              const hasEmbed = Boolean(video.embedHtml);

              return (
                <ResourceCard key={video.id}>
                  <h3>{video.title}</h3>
                  {hasEmbed ? (
                    <VideoEmbed dangerouslySetInnerHTML={{ __html: video.embedHtml ?? '' }} />
                  ) : (
                    video.thumbnailUrl && (
                      <Thumbnail src={video.thumbnailUrl} alt={`Prévia do vídeo ${video.title}`} />
                    )
                  )}
                  {video.description && <p>{video.description}</p>}
                  <ResourceMeta>
                    {video.updatedAt && <span>Publicado em {video.updatedAt}</span>}
                  </ResourceMeta>
                </ResourceCard>
              );
            })}
          </ResourceList>
        )}
      </Section>
    </Page>
  );
}
